import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { window } from "@swa-ui/browser";

import {
  buildIframeSrc,
  resolveBaseUrl,
  resolveLocale,
  sortEntriesByKey,
} from "../../buildIframeSrc";
import {
  IFRAME_SANDBOX,
  IFRAME_TITLE,
  INITIAL_HEIGHT,
  READY_FALLBACK_DELAY,
} from "../../defines/ppaHotelDefaults";
import { isHotelIFrameDataValid } from "../../isHotelIFrameDataValid";
import { getOrigin, processEvent } from "../../postMessageHandlers";

const IFRAME_STYLE = { border: "none", display: "block", width: "100%" };

/**
 * PPAHotelContainer renders a PPA (Partner Platforms by Agoda) hotel cross-sell iframe as a direct iframe element.
 * Search parameters are passed as URL query parameters. Iframe lifecycle events are received via postMessage
 * with origin validation, replacing the legacy TR (Testarossa) hosted loader script.
 */
export const PPAHotelContainer = (props) => {
  const {
    className,
    environmentTier,
    hotelIFrameData,
    language,
    layout,
    onError,
    onReady,
    onUpdate,
    queryParameters,
    sandbox = IFRAME_SANDBOX,
    searchCriteria = {},
    tag,
    title = IFRAME_TITLE,
  } = props;

  const { adults, checkinDate, checkoutDate, childGuests, currency, destination, rooms } =
    searchCriteria;
  const { cid, layout: layoutFromIFrameData, tag: tagFromIFrameData } = hotelIFrameData || {};
  const baseUrl = resolveBaseUrl(hotelIFrameData, environmentTier);
  const resolvedTag = tag ?? tagFromIFrameData;
  const resolvedLayout = layout ?? layoutFromIFrameData;
  const isConfigValid = isHotelIFrameDataValid(hotelIFrameData);

  const [iframeHeight, setIframeHeight] = useState(INITIAL_HEIGHT);
  const iframeRef = useRef(null);
  const readyFiredRef = useRef(false);
  const readyFallbackTimerRef = useRef(null);
  const onErrorRef = useRef(onError);
  const onReadyRef = useRef(onReady);
  const onUpdateRef = useRef(onUpdate);

  useEffect(() => {
    onErrorRef.current = onError;
    onReadyRef.current = onReady;
    onUpdateRef.current = onUpdate;
  }, [onError, onReady, onUpdate]);

  const queryParametersKey = useMemo(
    () => JSON.stringify(sortEntriesByKey(queryParameters)),
    [queryParameters]
  );

  const iframeSrc = useMemo(
    () =>
      buildIframeSrc({
        adults,
        checkinDate,
        checkoutDate,
        childGuests,
        currency,
        destination,
        environmentTier,
        hotelIFrameData,
        layout: resolvedLayout,
        locale: resolveLocale(language),
        queryParameters,
        rooms,
        tag: resolvedTag,
      }),
    [
      adults,
      baseUrl,
      checkinDate,
      checkoutDate,
      childGuests,
      cid,
      currency,
      destination,
      environmentTier,
      language,
      queryParametersKey,
      resolvedLayout,
      rooms,
      resolvedTag,
    ]
  );

  useEffect(() => {
    readyFiredRef.current = false;
    clearReadyFallback();
    setIframeHeight(INITIAL_HEIGHT);
  }, [iframeSrc]);

  useEffect(() => {
    const iframe = iframeRef.current;
    let cleanup;

    if (iframe) {
      const handleError = () => fireError("iframe-load-error");

      iframe.addEventListener("error", handleError);
      cleanup = () => iframe.removeEventListener("error", handleError);
    }

    return cleanup;
  }, [iframeSrc]);

  useEffect(() => {
    let cleanup;

    if (isConfigValid) {
      const expectedOrigin = getOrigin(baseUrl);

      const handleMessage = (event) => {
        if (event.origin === expectedOrigin) {
          const isFromThisIframe = event.source === iframeRef.current?.contentWindow;

          if (isFromThisIframe) {
            processEvent(event.data, getEventCallbacks());
          }
        }
      };

      window.addEventListener("message", handleMessage);
      cleanup = () => {
        window.removeEventListener("message", handleMessage);
        clearReadyFallback();
      };
    }

    return cleanup;

    function getEventCallbacks() {
      return {
        onError: fireError,
        onReady: fireReady,
        onResize: applyResize,
        onUpdate: onUpdateRef.current,
      };
    }

    function applyResize(height) {
      if (typeof height === "number" && height > 0) {
        setIframeHeight(height);
      }
    }
  }, [baseUrl, cid, isConfigValid]);

  return isConfigValid ? <iframe {...getIframeProps()} /> : null;

  function getIframeProps() {
    return {
      className,
      "data-cross-sell-layout": resolvedLayout,
      "data-cross-sell-placement": resolvedTag,
      onLoad: handleIframeLoad,
      ref: iframeRef,
      sandbox,
      src: iframeSrc,
      style: { ...IFRAME_STYLE, height: `${iframeHeight}px` },
      title,
    };
  }

  function handleIframeLoad() {
    if (!readyFiredRef.current) {
      clearReadyFallback();

      readyFallbackTimerRef.current = setTimeout(() => {
        fireReady({ isFallback: true });
      }, READY_FALLBACK_DELAY);
    }
  }

  function fireReady(payload) {
    if (!readyFiredRef.current) {
      readyFiredRef.current = true;
      clearReadyFallback();

      if (onReadyRef.current) {
        onReadyRef.current(payload);
      }
    }
  }

  function fireError(errorCode) {
    readyFiredRef.current = true;
    clearReadyFallback();

    if (onErrorRef.current) {
      onErrorRef.current(errorCode);
    }
  }

  function clearReadyFallback() {
    if (readyFallbackTimerRef.current) {
      clearTimeout(readyFallbackTimerRef.current);
      readyFallbackTimerRef.current = null;
    }
  }
};

PPAHotelContainer.propTypes = {
  /** Additional class for positioning the iframe. Applied to the iframe root element. */
  className: PropTypes.string,
  /** Environment tier override ("prod", "staging", etc.). Required — consumers must pass this explicitly. Vision SPAs should derive it from their own app config or hostname utilities; NextGen SPAs can use `getEnvironmentTier` from `@swa-ui/dotcom`. */
  environmentTier: PropTypes.string.isRequired,
  /** PPA bootstrap configuration. Must include `iframeUrl` (with `prod` and `staging` keys) and `cid`. May include default `tag` and `layout`. */
  hotelIFrameData: PropTypes.shape({
    cid: PropTypes.string,
    iframeUrl: PropTypes.shape({
      prod: PropTypes.string,
      staging: PropTypes.string,
    }),
    layout: PropTypes.string,
    tag: PropTypes.string,
  }),
  /** Language override. "es" forces Spanish locale; otherwise locale is auto-detected from the page hostname. */
  language: PropTypes.oneOf(["en", "en_us", "es"]),
  /** Iframe rendering mode override. Falls back to `hotelIFrameData.layout` when not provided. */
  layout: PropTypes.string,
  /** Callback invoked when the iframe fails. Receives the vendor's error code on `crossSellError` postMessage events, or the string `"iframe-load-error"` on network-level failures (DNS failure, connection refused, 404). Firing an error cancels the pending `onLoad` ready-fallback for this navigation, so `onReady` will not fire afterwards unless the iframe re-navigates. */
  onError: PropTypes.func,
  /** Callback invoked when the iframe is ready to be shown. Receives the vendor's `crossSellReady` payload (typically `{ layout, placement }`) when the vendor fires the event, or `{ isFallback: true }` when the iframe's `onLoad` event triggers the fallback because the vendor never sent `crossSellReady`. Consumers can distinguish the two by checking `payload.isFallback`. Fires again each time the iframe re-navigates (e.g. `searchCriteria` changes). */
  onReady: PropTypes.func,
  /** Callback invoked with the vendor `data` payload when a `crossSellUpdate` postMessage event is received (fires after completed form changes; not sent for every keystroke). */
  onUpdate: PropTypes.func,
  /** Additional pass-through query parameters appended to the iframe `src`. Intended for marketing attribution (e.g. `clk`, `label`, `utm_*`). Reserved keys owned by the builder (`cid`, `adults`, `currency`, `destination`, `checkin`, `checkout`, `layout`, `tag`, `rooms`, `children`) are always ignored, so any caller-supplied entry with those keys is dropped. Object identity does not need to be stable across renders — reference changes with the same values will not force a rebuild. */
  queryParameters: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  /** Space-separated `<iframe sandbox>` value applied to the iframe. Defaults to `IFRAME_SANDBOX` from defines (`allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox`). Override with a narrower or wider value only after confirming what the vendor iframe needs. */
  sandbox: PropTypes.string,
  /** The user's hotel search intent — passed to PPA as URL query parameters. All fields optional; sensible defaults applied. */
  searchCriteria: PropTypes.shape({
    /** Number of adult guests. Integer 1-9. Defaults to 1 when omitted. */
    adults: PropTypes.number,
    /** Check-in date in YYYY-MM-DD format. Omitted from the iframe URL when not provided. */
    checkinDate: PropTypes.string,
    /** Check-out date in YYYY-MM-DD format. Omitted from the iframe URL when not provided. */
    checkoutDate: PropTypes.string,
    /** Number of child guests, sent to PPA as the `children` query parameter. Integer 0-17. Omitted when not provided or outside the valid range. */
    childGuests: PropTypes.number,
    /** Currency code for pricing. Defaults to "USD" when omitted. */
    currency: PropTypes.string,
    /** Destination airport code passed as the `destination` query parameter. */
    destination: PropTypes.string,
    /** Number of rooms. Integer 1-9. Omitted when not provided or outside the valid range. */
    rooms: PropTypes.number,
  }),
  /** Attribution identifier passed to the vendor as their `tag` query param. Overrides `hotelIFrameData.tag` when provided. */
  tag: PropTypes.string,
  /** Accessible `<iframe>` title. Provide distinct values when rendering more than one PPA hotel iframe on the same page (e.g. confirmation above + below fold) so assistive tech can distinguish them. Defaults to `"Hotel search iframe"`. */
  title: PropTypes.string,
};
